import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import {
  setHeaderConfigsAction,
  setHeaderTitleAction
} from '../../../reactRedux/Actions/headerAction';
import { useDispatch } from 'react-redux';
import styles from './styles.module.scss';
import StepperIndicator from '../../common/StepperIndicator';
import RtlWrapper from '../../../helpers/RtlWrapper';
import ArrowForwardIosRoundedIcon from '@material-ui/icons/ArrowForwardIosRounded';
import ArrowBackIosRoundedIcon from '@material-ui/icons/ArrowBackIosRounded';
import cx from 'classnames';
import Loader from '../../common/Loader';
import FormControl from '@material-ui/core/FormControl';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Checkbox from '@material-ui/core/Checkbox';
import { routes } from '../../../assets/routes';
import { Helmet } from 'react-helmet';
import useDesktopDisplay from '../../../hooks/useDesktopDisplay';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getSurveyQuestionsRequest,
  getSurveyRequest,
  surveyAnswerRequest,
  surveyResponseRequest,
  surveySubmitRequest
} from './api';
import { getFeatureListRequest } from './../../../helpers/api';
import { Alert, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { useTranslation } from '../../../i18n/LanguageProvider';

const Survey = () => {
  const { t, isRtl } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const setHeader = (title) => dispatch(setHeaderTitleAction(title));
  const setHeaderConfigs = (configs) =>
    dispatch(setHeaderConfigsAction(configs));
  const isDesktop = useDesktopDisplay();

  const [uneditable, setUneditable] = useState(
    history.location?.state?.uneditable || false
  );
  const [startSurvey, setStartSurvey] = useState(false);
  const [surveyData, setSurveyData] = useState({});
  const [surveyQuestion, setSurveyQuestion] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState({});
  const [surveyResponseId, setSurveyResponseId] = useState(null);
  const [answer, setAnswer] = useState('');
  const [selectedOptionIds, setSelectedOptionIds] = useState([]);
  const [steps, setSteps] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const surveyId = history.location.pathname.split('/')[2];
  // const uneditable = history.location.state.uneditable

  const { isLoading: surveyIsLoading } = useQuery(
    ['survey', surveyId],
    () => getSurveyRequest(surveyId),
    {
      onSuccess(response) {
        setUneditable(
          new Date(response.data.end_at).setHours(0, 0, 0, 0) <
            new Date().setHours(0, 0, 0, 0) ||
            response.data.has_response_submitted
        );
        setSurveyData(response.data);
      },
      onError(err) {
        toast.error(err.response?.data?.detail);
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false
    }
  );

  const { isLoading: realTimeSignalIsLoading, data: realTimeSignal } = useQuery(
    ['featureList'],
    getFeatureListRequest,
    {
      select(data) {
        return data.data.filter(
          (value) => value.feature_name === 'real_time_signal'
        )[0].active;
      },
      onSuccess(data) {
        if (data) {
          setCurrentQuestion(surveyQuestion[currentStep]);
          setStartSurvey(true);
          getResponseId();
        }
      }
    }
  );

  useQuery(
    ['surveyQuestions', surveyId, surveyResponseId],
    () => getSurveyQuestionsRequest(surveyId, surveyResponseId),
    {
      enabled: enabled,
      onSuccess({ data }) {
        setSurveyQuestion(data);
        setCurrentQuestion(data[0]);
        setSteps(data.length);
        if (data[0].answers.length > 0) {
          if (data[0].answers[0].answer) {
            setAnswer(data[0].answers[0].answer);
          } else {
            setSelectedOptionIds(
              data[0].answers.map((option) => option.selected_option.id)
            );
          }
        } else {
          setSelectedOptionIds([]);
          setAnswer('');
        }
      },
      onError(err) {
        toast.error(err.response?.data?.detail);
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false
    }
  );

  const surveyResponseRequestMutation = useMutation(surveyResponseRequest, {
    onSuccess({ data: { id } }) {
      setSurveyResponseId(id);
      setEnabled(true);
    },
    onError(err) {
      toast.error(err.response?.data?.detail);
    }
  });

  const surveySubmitRequestMutation = useMutation(
    () => surveySubmitRequest(surveyResponseId),
    {
      onSuccess() {
        toast.success(t('survey.sent'));
        history.push(routes.SURVEYS);
      },
      onError(err) {
        toast.error(err.response?.data?.detail);
      }
    }
  );

  const surveyAnswerRequestMutation = useMutation(surveyAnswerRequest, {
    onSuccess(response) {
      let temp = surveyQuestion;
      temp[currentStep].answers = response.data;
      setSurveyQuestion(temp);
      if (currentStep < surveyQuestion.length - 1) {
        setCurrentStep(currentStep + 1);
        setCurrentQuestion(surveyQuestion[currentStep + 1]);
        if (surveyQuestion[currentStep + 1].answers.length > 0) {
          if (surveyQuestion[currentStep + 1].answers[0].answer) {
            setAnswer(surveyQuestion[currentStep + 1].answers[0].answer);
          } else {
            setSelectedOptionIds(
              surveyQuestion[currentStep + 1].answers.map(
                (option) => option.selected_option.id
              )
            );
          }
        } else {
          setSelectedOptionIds([]);
          setAnswer('');
        }
      }
      if (
        currentStep === surveyQuestion.length - 1 &&
        currentQuestion.type_repr.en !== 'single_choice'
      ) {
        surveySubmitRequestMutation.mutate();
      }
    },
    onError(err) {
      toast.error(err.response?.data?.detail);
    }
  });

  const sendResponse = () => {
    if (
      currentQuestion.type_repr.en === 'short_answer' ||
      currentQuestion.type_repr.en === 'long_answer'
    ) {
      if (currentQuestion.required && answer === '') {
        toast.error(t('survey.answerRequired'));
        return;
      }
      surveyAnswerRequestMutation.mutate({
        answer: answer,
        survey_response_id: surveyResponseId,
        survey_question_id: currentQuestion.id
      });
    } else if (currentQuestion.type_repr.en === 'multiple_choice') {
      if (currentQuestion.required && selectedOptionIds.length === 0) {
        toast.error(t('survey.optionRequired'));
        return;
      }
      surveyAnswerRequestMutation.mutate({
        survey_response_id: surveyResponseId,
        survey_question_id: currentQuestion.id,
        selected_option_ids: selectedOptionIds
      });
    } else if (currentQuestion.type_repr.en === 'single_choice') {
      if (currentQuestion.required && selectedOptionIds.length === 0) {
        toast.error(t('survey.optionRequired'));
        return;
      }
      surveySubmitRequestMutation.mutate();
    }
  };
  const getResponseId = () => {
    if (!uneditable) {
      surveyResponseRequestMutation.mutate({ survey_id: surveyId });
    } else {
      setEnabled(true);
    }
  };

  useEffect(() => {
    setHeader(t('survey.title'));
    setHeaderConfigs({ credits: false, exit: false, back: true, logo: false });
  }, []);

  const disableNextStep =
    currentQuestion?.type_repr?.en === 'short_answer' ||
    currentQuestion?.type_repr?.en === 'long_answer'
      ? answer === '' && currentQuestion?.required
      : selectedOptionIds?.length === 0 && currentQuestion?.required;

  return (
    <>
      <div
        className={cx(
          styles.page,
          styles.full,
          styles.noPaddingTop,
          styles.startContainer
        )}
      >
        <Helmet>
          <title>{t('survey.title')}</title>
        </Helmet>

        {isDesktop && (
          <div className={styles.pageHeader}>{t('survey.title')}</div>
        )}

        {surveyIsLoading && realTimeSignalIsLoading ? (
          <Loader size={40} />
        ) : startSurvey ? (
          currentQuestion ? (
            <>
              <h3 style={{ whiteSpace: 'pre-wrap' }}>
                {!realTimeSignal && `${currentQuestion.position}.   `}
                {currentQuestion?.required && (
                  <Typography
                    color={'error'}
                    fontWeight={'bold'}
                    component={'span'}
                  >
                    *{' '}
                  </Typography>
                )}
                {currentQuestion.description}
              </h3>
              {currentQuestion.type_repr.en === 'short_answer' ? (
                <>
                  <TextField
                    id="short_answer"
                    className={styles.input}
                    onChange={
                      !uneditable
                        ? (e) => {
                            setAnswer(e.target.value);
                          }
                        : null
                    }
                    hiddenLabel
                    value={answer}
                    required
                  />
                  {!(currentStep === surveyQuestion.length - 1) && !uneditable && (
                    <div>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={sendResponse}
                      >
                        {t('common.confirm')}
                      </Button>
                    </div>
                  )}
                </>
              ) : currentQuestion.type_repr.en === 'long_answer' ? (
                <>
                  <TextField
                    id="long_answer"
                    multiline
                    rows={4}
                    className={styles.input}
                    onChange={
                      !uneditable
                        ? (e) => {
                            setAnswer(e.target.value);
                          }
                        : null
                    }
                    hiddenLabel
                    required
                    value={answer}
                  />
                  {!(currentStep === surveyQuestion.length - 1) && !uneditable && (
                    <div>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={sendResponse}
                      >
                        {t('common.confirm')}
                      </Button>
                    </div>
                  )}
                </>
              ) : currentQuestion.type_repr.en === 'single_choice' ? (
                <FormControl disabled={uneditable}>
                  <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    defaultValue="female"
                    name="radio-buttons-group"
                    value={
                      !!currentQuestion.answers[0] &&
                      currentQuestion.answers[0].selected_option.description
                    }
                  >
                    {currentQuestion.options.map((option, i) => (
                      <>
                        <FormControlLabel
                          key={i}
                          checked={selectedOptionIds[0] === option.id}
                          onChange={() => {
                            setSelectedOptionIds([option.id]);
                            surveyAnswerRequestMutation.mutate({
                              survey_response_id: surveyResponseId,
                              survey_question_id: currentQuestion.id,
                              selected_option_ids: [option.id]
                            });
                          }}
                          control={<Radio />}
                          id={option.id}
                          label={option.description}
                        />
                      </>
                    ))}
                  </RadioGroup>
                </FormControl>
              ) : currentQuestion.type_repr.en === 'multiple_choice' ? (
                <FormControl>
                  <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    defaultValue="female"
                    name="radio-buttons-group"
                  >
                    {currentQuestion.options.map((option, i) => (
                      <FormControlLabel
                        disabled={uneditable}
                        key={i}
                        value={option.description}
                        checked={selectedOptionIds.indexOf(option.id) > -1}
                        onChange={() => {
                          if (selectedOptionIds.includes(option.id)) {
                            setSelectedOptionIds(
                              selectedOptionIds.filter(
                                (item) => option.id !== item
                              )
                            );
                          } else {
                            setSelectedOptionIds([
                              ...selectedOptionIds,
                              option.id
                            ]);
                          }
                        }}
                        control={<Checkbox />}
                        label={option.description}
                      />
                    ))}
                  </RadioGroup>
                  {!(currentStep === surveyQuestion.length - 1) && !uneditable && (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={sendResponse}
                    >
                      {t('common.confirm')}
                    </Button>
                  )}
                </FormControl>
              ) : null}
            </>
          ) : null
        ) : (
          !realTimeSignal && (
            <>
              <h2 dir="auto">{surveyData.title}</h2>
              <p
                className={cx(styles.text)}
                style={{ fontWeight: 'bold', whiteSpace: 'pre-wrap' }}
                dir="auto"
              >
                {surveyData?.description}
              </p>
              <Alert
                variant="outlined"
                color={surveyData?.anonymous ? 'info' : 'error'}
                severity={surveyData?.anonymous ? 'info' : 'error'}
                sx={{
                  width: 'fit-content',
                  marginInline: 'auto',
                  mb: 2
                }}
              >
                {surveyData?.anonymous
                  ? t('survey.anonymousNotice')
                  : t('survey.identifiedNotice')}
              </Alert>
              <Button
                fullWidth
                size="large"
                variant="contained"
                onClick={() => {
                  setCurrentQuestion(surveyQuestion[currentStep]);
                  setStartSurvey(true);
                  getResponseId();
                }}
              >
                {uneditable ? t('survey.view') : t('survey.start')}
              </Button>
            </>
          )
        )}
        {currentStep === surveyQuestion.length - 1 && !uneditable && (
          <div className={styles.btn}>
            <Button fullWidth variant="outlined" onClick={sendResponse}>
              {realTimeSignal
                ? t('survey.sendAnswer')
                : t('survey.sendAnswers')}
            </Button>
          </div>
        )}
        {startSurvey && steps > 1 && (
          <div className={styles.actionBar}>
            <div className={styles.slideIndicator}>
              <StepperIndicator steps={steps} currentStep={currentStep} />
            </div>
            <RtlWrapper>
              {currentStep > 0 && (
                <Button
                  variant="text"
                  startIcon={
                    isRtl ? (
                      <ArrowForwardIosRoundedIcon />
                    ) : (
                      <ArrowBackIosRoundedIcon />
                    )
                  }
                  onClick={() => {
                    setCurrentStep(currentStep - 1);
                    setCurrentQuestion(surveyQuestion[currentStep - 1]);
                    if (surveyQuestion[currentStep - 1].answers.length > 0) {
                      if (surveyQuestion[currentStep - 1].answers[0].answer) {
                        setAnswer(
                          surveyQuestion[currentStep - 1].answers[0].answer
                        );
                      } else {
                        setSelectedOptionIds(
                          surveyQuestion[currentStep - 1].answers.map(
                            (option) => option.selected_option.id
                          )
                        );
                      }
                    } else {
                      setSelectedOptionIds([]);
                      setAnswer('');
                    }
                  }}
                  style={{ marginInlineEnd: 'auto' }}
                >
                  {t('common.previous')}
                </Button>
              )}
              {currentStep < steps - 1 && (
                <Button
                  variant="text"
                  endIcon={
                    isRtl ? (
                      <ArrowBackIosRoundedIcon />
                    ) : (
                      <ArrowForwardIosRoundedIcon />
                    )
                  }
                  onClick={() => {
                    setCurrentStep(currentStep + 1);
                    setCurrentQuestion(surveyQuestion[currentStep + 1]);
                    if (currentStep + 1 < surveyQuestion.length) {
                      if (surveyQuestion[currentStep + 1].answers.length > 0) {
                        if (surveyQuestion[currentStep + 1].answers[0].answer) {
                          setAnswer(
                            surveyQuestion[currentStep + 1].answers[0].answer
                          );
                        } else {
                          setSelectedOptionIds(
                            surveyQuestion[currentStep + 1].answers.map(
                              (option) => option.selected_option.id
                            )
                          );
                        }
                      } else {
                        setSelectedOptionIds([]);
                        setAnswer('');
                      }
                    }
                  }}
                  style={{ marginInlineStart: 'auto' }}
                  disabled={disableNextStep}
                >
                  {t('common.next')}
                </Button>
              )}
            </RtlWrapper>
          </div>
        )}
      </div>
    </>
  );
};

export default Survey;
